import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router"
import { DateTime } from "luxon"
import { Loading, Popup, ConfirmDialog } from "components"
import SampleRowElement from "./SampleRowElement"

import "./index.scss"

import { Button } from "react-bootstrap"
import InputGroup from "react-bootstrap/InputGroup"
import Form from "react-bootstrap/Form"
import Table from "react-bootstrap/Table"
import { Text, Container } from "components/Form"
import AddEditSamplePopup from "./AddEditSamplePopup"
import { FiInfo, FiAlertTriangle } from "react-icons/fi"
// NB: `analysisDefinitions` is aliased — this component has a local `analysisDefinitions`
// selector value (the definitions map) that would shadow the module.
import { sampleView, samples, message, analysisDefinitions as analysisDefinitionsActions, limsResults, limsOos, limsRelease, limsSampleQc, limsBatchHold, limsReruns, limsReviews } from "state_management"
import useT from "utils/hooks/useT"
import useLocalStorage from "utils/hooks/useLocalStorage"

import isOnMobile from "utils/scripts/isOnMobile"
import { isWithinOneDayLockGrace } from "utils/scripts/lockGrace"
import { dynamicSortMultiple } from "utils/scripts/sort"
import OosInvestigationModal from "components/Lims/OosInvestigationModal"

export default function SampleView() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useT("messages")
  const { t: tc } = useT("common")
  const isLoading = useSelector((state) => state.sampleView.isLoading)
  const needsRefresh = useSelector((state) => state.sampleView.needsRefresh)
  const sampleGroups = useSelector((state) => state.sampleView.sampleGroups)
  const sampleGroupsSortedByProduct = useSelector((state) => state.sampleView.sampleGroupsSortedByProduct)
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone)
  const members = useSelector((state) => state.members)
  const isViewOnly = useSelector((state) => state.auth.isViewOnly)
  const isMember = useSelector((state) => state.auth.isMember)
  const allowTaskTransfer = useSelector((state) => state.teamStatus.allowTaskTransfer)
  const isReadOnly = isViewOnly || (isMember && !allowTaskTransfer)
  const showLims = useSelector((state) => state.teamStatus.showLims)
  const shouldBeDoneSamples = useSelector((state) => state.sampleView.shouldBeDoneSamples)
  const analysisDefinitions = useSelector((state) => state.analysisDefinitions)
  const productCollection = useMemo(() => {
    const set = new Set()
    ;(sampleGroups || []).forEach((s) => set.add(s.productName || ""))
    return Array.from(set).map((p) => ({ id: p, name: p === "" ? tc("samplesWithoutProduct") : p }))
  }, [sampleGroups, tc])

  // Build collection for analyses used in the Add Sample popup
  const analysisCollection = useMemo(() => {
    const defs = analysisDefinitions || {}
    return Object.values(defs).map((a) => ({
      // prefer explicit id fields (analysisId or id) so selects return ids instead of names
      id: a.analysisId || a.id || a.name || a.analysis_name || a.analysisName,
      name: a.display_name || a.displayName || a.name || a.analysis_name || a.analysisName || String(a.id),
    }))
  }, [analysisDefinitions])

  // SPEC-038 (F6): the QA SampleReview worklist deep-links here as "?sample=<sampleName>" to land
  // QA on the exact samplenr/lotnr for review. Seed the filter from it once (lazy init); the field
  // stays freely editable afterwards. No param → empty, exactly as before.
  const [filterString, setFilterString] = useState(() => {
    try {
      return new URLSearchParams(location.search).get("sample") || ""
    } catch {
      return ""
    }
  })
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  // SPEC-032: non-LIMS teams keep the legacy per-sample "Fail Sample" → rerun action (LIMS teams
  // fail via the QC Results & Approvals / OOS flow). Gated on !showLims at the button below.
  const [failConfirm, setFailConfirm] = useState(false)
  const [pendingDeleteSampleId, setPendingDeleteSampleId] = useState(null)

  const [activeSample, setActiveSample] = useState("")
  const [samplePriority, setSamplePriority] = useState(null)
  const [activeSampleAnalysis, setActiveSampleAnalysis] = useState({})
  // SPEC-027 (RR4 #2): GMP-guard — block unplanning the active sample when its analysis is in
  // progress (has any QC result) or the sample is DONE. Mirrors the SampleTable guard; authoritative
  // enforcement is backend-side.
  // SPEC-044: explicitly gated on showLims (feeds unplanBlocked, itself showLims-gated) — a non-LIMS
  // team must never derive block-state from the LIMS results slice; the gate is explicit, not a
  // reliance on limsResults.items being empty.
  const activeAnalysisHasResults = useSelector((state) =>
    showLims && activeSampleAnalysis?.analysisId
      ? state.limsResults.items.some((r) => r.analysisId === activeSampleAnalysis.analysisId)
      : false
  )
  // SPEC-045 (K2): a hard-locked analysis (samplesLocked) refuses single-sample unplan for BOTH
  // team types — the backend lock guard applies to LIMS and non-LIMS alike. Read the shared
  // samplesLocked flag off the planned analysis (same source as the sample-popup lock icon).
  const activeAnalysisLocked = useSelector((state) =>
    activeSampleAnalysis?.analysisId
      ? !!state.analyses.plannedAnalyses[activeSampleAnalysis.analysisId]?.samplesLocked
      : false
  )
  // SPEC-046 (F4): reason-aware lock tooltip — an auto-locked PAST analysis cannot simply be
  // unlocked (SPEC-045 K14 refuses that), so "unlock it to make changes" would be dead-end advice;
  // point at moving the analysis forward instead. Reads the authoritative earliestTaskStart
  // (backend SPEC-047) and compares per render against today 00:00 team tz (the backend lock
  // boundary), so the verdict never goes stale across midnight.
  const activeAnalysisEarliestTaskStart = useSelector((state) =>
    (activeSampleAnalysis?.analysisId &&
      state.analyses.plannedAnalyses[activeSampleAnalysis.analysisId]?.earliestTaskStart) ||
    null
  )
  const activeAnalysisStarted =
    activeAnalysisEarliestTaskStart != null &&
    DateTime.fromISO(activeAnalysisEarliestTaskStart) < DateTime.now().setZone(teamTimeZone).startOf("day")
  // SPEC-046 follow-up (adversarial review): activeAnalysisStarted OR'ed in as the date backstop —
  // the backend unplan guard refuses ANALYSIS_TASK_IN_PAST regardless of the lock flag (R2), so the
  // ghost must fire on either signal (flag drift / popup open across midnight).
  // SPEC-053 (R20): both lock signals are lifted while the analysis' earliest task started
  // YESTERDAY (the one-day manual grace, shared window helper) — the LIMS terms stay absolute:
  // QC data and DONE samples are never unplannable, in or out of the window.
  const withinUnplanGrace = isWithinOneDayLockGrace(
    { earliestTaskStart: activeAnalysisEarliestTaskStart },
    teamTimeZone
  )
  const unplanBlocked =
    ((activeAnalysisLocked || activeAnalysisStarted) && !withinUnplanGrace) ||
    (showLims && (activeAnalysisHasResults || String(activeSampleAnalysis?.status) === "DONE"))
  const [sortByProduct, setSortByProduct] = useLocalStorage("sampleView-sortByProduct", false)
  const [includeDone, setIncludeDone] = useLocalStorage("sampleView-includeDone", false)
  const sampleViewRef = useRef(null);
  const shouldShowUnplannedPopup = Object.values(activeSampleAnalysis).length !== 0 && !activeSampleAnalysis?.analysesPlanned;

  // SPEC-018: the OOS investigation the modal is currently showing (its OOS record id), or null.
  const [activeOosId, setActiveOosId] = useState(null)

  // Add-sample popup state and form
  const [showAddSample, setShowAddSample] = useState(false)
  // showMore removed - we don't need local expanded state in this file now
  const [newSample, setNewSample] = useState({
    sampleName: "",
    analysisName: [],
    observationId: "1",
    numberOfObservations: 1,
    arrival: DateTime.now().setZone(teamTimeZone).startOf('day').toISO(),
    arrivalTime: "00:00",
    // Keep date undefined until user sets it, but default time visually to 23:59
    deadline: "",
    deadlineTime: "23:59",
    priority: 2,
    status: "TODO",
    productName: "",
    exclusive: false,
    isFlagged: false,
  })

  // submitNewSample removed; new flow uses AddEditSamplePopup and submitNewSampleFromPopup

  const resetNewSampleForm = () => {
    setShowAddSample(false)
    setNewSample({
      sampleName: "",
      analysisName: [],
      observationId: "1",
      numberOfObservations: 1,
      arrival: DateTime.now().setZone(teamTimeZone).startOf('day').toISO(),
      arrivalTime: "00:00",
      deadline: "",
      deadlineTime: "23:59",
      priority: 2,
      status: "TODO",
      productName: null,
      exclusive: false,
      uploadConfigId: "",
      isFlagged: false,
    })
  }

  const submitNewSampleFromPopup = (payload) => {
    // payload comes from AddEditSamplePopup: arrival/deadline already ISO strings
    // We expect camelCase keys from the popup: analysisIds, observationId
    const rawAnalyses = payload.analysisIds || []
    const hasAnalysis = Array.isArray(rawAnalyses) ? rawAnalyses.filter(Boolean).length > 0 : !!rawAnalyses
    if (!payload.sampleName || !hasAnalysis || !payload.arrival || !payload.deadline) {
      console.log('submitNewSampleFromPopup: missing fields', { sampleName: payload.sampleName, hasAnalysis, arrival: payload.arrival, deadline: payload.deadline, rawAnalyses })
      dispatch(message.warning(t("adhoc.fillRequiredFields")))
      return
    }

    // Ensure analysisIds contain only ids. Map any names to ids using analysisCollection if necessary
    const analysisIds = (Array.isArray(rawAnalyses) ? rawAnalyses : [rawAnalyses])
      .filter(Boolean)
      .map((v) => {
        const foundById = analysisCollection.find((ac) => String(ac.id) === String(v))
        if (foundById) return foundById.id
        const foundByName = analysisCollection.find((ac) => ac.name === v || String(ac.name) === String(v))
        if (foundByName) return foundByName.id
        return v
      })

    const input = {
      sampleName: payload.sampleName,
      analysisIds: analysisIds,
      observationId: payload.observation_id || payload.observationId || newSample.observationId,
      numberOfObservations: payload.numberOfObservations || newSample.numberOfObservations,
      arrival: payload.arrival,
      deadline: payload.deadline,
      priority: parseInt(payload.priority, 10),
      status: payload.status,
      productName: payload.productName || null,
      exclusive: !!payload.exclusive,
      isFlagged: !!payload.isFlagged,
    }

    // Return the dispatch promise (resolving to the created sample) so the popup can chain on it
    // — LIMS F-C1 uses it to persist the new sample's QC details against the created sample id.
    // The non-LIMS path ignores the return value, so behaviour there is unchanged.
    return dispatch(samples.createSample(input)).then((res) => {
      setShowAddSample(false)
      setNewSample({
        sampleName: "",
        analysisName: [],
        observationId: "1",
        numberOfObservations: 1,
        arrival: DateTime.now().setZone(teamTimeZone).startOf('day').toISO(),
        arrivalTime: "00:00",
        deadline: "",
        deadlineTime: "23:59",
        priority: 2,
        status: "TODO",
        productName: null,
        exclusive: false,
        uploadConfigId: "",
        isFlagged: false,
      })
      return res
    }).catch(() => {})
  }

  useEffect(() => {
    if (needsRefresh) {
      dispatch(sampleView.fetch())
    }
  }, [needsRefresh, dispatch])

  // LIMS F-C4: load the data the per-sample release status + QA release action depend on.
  // SPEC-010 (Area 4): also load the sample-QC details (for batch/lot) + the batch holds, so a
  // rejected batch surfaces the BATCH_REJECTED status + STOP icons here.
  // SPEC-011 (bug #13): also load the QC-task links (reviews + corrections) and the review specs
  // so the right-panel Reviews inbox and the review/correction flow are populated when an analyst
  // opens the plan straight from the Sample View without first visiting the LIMS workspace.
  useEffect(() => {
    if (showLims) {
      dispatch(limsResults.fetch())
      dispatch(limsOos.fetch())
      dispatch(limsRelease.fetch())
      dispatch(limsSampleQc.fetch())
      dispatch(limsBatchHold.fetch())
      dispatch(limsReruns.fetch())
      dispatch(limsReviews.fetch())
      // SPEC-021: the review specs ride on the analysis-definition payload (B2) and the QC
      // capabilities on the member dump (B3) — one definitions fetch covers the former, the
      // members are already loaded app-wide.
      dispatch(analysisDefinitionsActions.fetch())
    }
  }, [showLims, dispatch])

  // RR2/RR3 #7: make every blue sample-name box as wide as the WIDEST box's NATURAL content (name +
  // its own icons), so the column is uniform AND grows when any row gains an icon (e.g. priority).
  // Each box is width:max-content; we measure the widest and lift them all to it via a CSS var on the
  // scroll container. RR3: the status icons load in ASYNCHRONOUSLY (LIMS fetches), so a one-shot
  // measure left the boxes equal at first and then drifting apart as icons appeared. A ResizeObserver
  // now re-measures whenever a box actually changes size. We pause the observer around our own
  // measurement (which toggles min-width) and only write the var when it changes, so it cannot loop.
  useLayoutEffect(() => {
    const root = sampleViewRef.current
    if (!root) return undefined
    const boxes = root.querySelectorAll(".samplegroup-row > h3")
    if (!boxes.length) return undefined

    const measure = () => {
      let widest = 0
      boxes.forEach((b) => {
        const prevMin = b.style.minWidth
        b.style.minWidth = "0px" // ignore the shared floor while measuring this box's natural width
        if (b.offsetWidth > widest) widest = b.offsetWidth
        b.style.minWidth = prevMin
      })
      if (widest <= 0) return
      const next = Math.ceil(widest) + "px"
      if (root.style.getPropertyValue("--lims-box-w") !== next) {
        root.style.setProperty("--lims-box-w", next)
      }
    }

    let frame = 0
    let observer = null
    const observeAll = () => {
      boxes.forEach((b) => observer.observe(b))
    }
    const remeasure = () => {
      if (observer) observer.disconnect() // pause so our own min-width toggling isn't observed
      measure()
      observeAll()
    }
    observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(remeasure)
    })

    measure()
    observeAll()

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [sampleGroups, sampleGroupsSortedByProduct, sortByProduct, filterString, includeDone])

  useEffect(() => {
    let timer;
    if (shouldShowUnplannedPopup) {
      // Set the timeout to hide the popup
      timer = setTimeout(() => {
        setActiveSampleAnalysis({});
      }, 4000);

      const handleScroll = () => {
        setActiveSampleAnalysis({});
        clearTimeout(timer);
      };

      // Ensure the element exists before adding the event listener
      const element = sampleViewRef.current;
      if (element) {
        element.addEventListener('scroll', handleScroll, { passive: true });
      }

      // Cleanup function
      return () => {
        clearTimeout(timer);
        if (element) {
          element.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [shouldShowUnplannedPopup]);

  const filteredSampleGroups = useMemo(() => {
    // Ensure the arrays are not null before proceeding
    if (!sampleGroups || !sampleGroupsSortedByProduct) {
      return [];
    }

    // Filter the sample groups based on the filterString and remove null values
    let sortedSampleGroups = (!sortByProduct ? sampleGroups : sampleGroupsSortedByProduct)
    .map((product) => {
      if (!sortByProduct) {
        return filterString ? (product.sampleName.toUpperCase().includes(filterString.toUpperCase()) ? product : null) : product;
      } else {
        const [key, value] = Object.entries(product)[0];
        const filteredSamples = key.toUpperCase().includes(filterString.toUpperCase())
          ? value
          : value.filter((sample) => sample.sampleName.toUpperCase().includes(filterString.toUpperCase()));
        const isAllDone = filteredSamples.every((sample) => sample.isDone);
        if (!includeDone && isAllDone) {
          return null;
        }
        return filteredSamples.length > 0
          ? { [key]: filteredSamples.filter((sample) => includeDone || !sample.isDone) }
          : null;
      }
    })
    .filter((product) => product !== null);

    // Sort the filtered sample groups by minDeadline and sampleName
    if (!sortByProduct) {
      // Sort the whole array by sampleName if not sorting by product
      sortedSampleGroups = sortedSampleGroups.sort(dynamicSortMultiple('minDeadline', 'sampleName'));
    } else {
      // When sorting by product, sort each product group's samples
      sortedSampleGroups = sortedSampleGroups.map((group) => {
        const [productName, samples] = Object.entries(group)[0];

        // Sort the samples within this group
        const sortedSamples = samples.sort(dynamicSortMultiple('minDeadline', 'sampleName'));

        // Return the sorted group with the product name key
        return { [productName]: sortedSamples };
      });
    }

    return sortedSampleGroups;
  }, [sampleGroups, sampleGroupsSortedByProduct, sortByProduct, filterString, includeDone]);

  if (isLoading || needsRefresh) {
    return <Loading title={tc("loadingSamples")} />
  }

  const initialValue = 0
  // Compute ONE width for every blue sample-name box (uniform column), driven by the LONGEST sample
  // name so the widest box sets all the others. `ch` (the width of "0" in the box font) tracks the
  // actual rendered text far better than `rem`, so the box hugs the content. Plus the deadline
  // (85px) and the icon area (LIMS icons + priority/warning group + gaps ≈ 120px). Capped at 50%.
  const maxWidth =
    "min(calc(" +
      sampleGroups.reduce((acc, curr) => Math.max(curr.sampleName.length, acc), initialValue) +
      "ch + 120px + 85px), 50%)"


  // SPEC-020 (C5): flagging is a pure flag again — it no longer raises a LIMS OOS/OOT register
  // entry. Records raised here had no Phase-1 investigation, no UI and no close path (orphans);
  // a formal OOS is opened ONLY via the investigation modal (FAIL card / QA worklist).
  const handleFlagSample = () => {
    dispatch(samples.flagSample(activeSampleAnalysis.sampleId, activeSampleAnalysis.sampleName))
    setActiveSampleAnalysis({})
  }

  const handleUnflagSample = () => {
    dispatch(samples.unflagSample(activeSampleAnalysis.sampleId, activeSampleAnalysis.sampleName))
    setActiveSampleAnalysis({})
  }

  // RR4 (#1): jump from this planned-sample popup to the analysis ON THE PLAN — its "QC Results &
  // Approvals" section, the normal place to enter QC results. Reuses the QA-worklist deep-link
  // (router to the plan at "/" + openReviewAnalysis, which jumps to the task's week + focuses QC).
  // Available to everyone incl. view-only: it opens the analysis, never edits it.
  const handleGoToAnalysis = () => {
    const analysisId = activeSampleAnalysis.analysisId
    setActiveSampleAnalysis({})
    if (!analysisId) return
    navigate("/")
    dispatch(limsReviews.openReviewAnalysis(analysisId))
  }

  // RR2 (#9 / Q2): the in-modal "Fail Sample" + "Open OOS investigation" actions were removed.
  // A FAIL is a result judgement, and the OOS investigation is now opened from the FAIL result in
  // "QC Results & Approvals" (ResultEntryPanel). The sample-row OOS badge still reopens an existing
  // investigation via setActiveOosId below.

  if (sampleGroups.length !== 0) {

    return (
      <>
        <div className="SampleView" ref={sampleViewRef}>
          <div className="sampleView-overhead-container">
            <div className="sampleview-filter-box" style={{ width: `calc(${maxWidth} - 3rem)` }}>
              <InputGroup>
                <Text
                  placeholder={sortByProduct ? tc("filterBySampleProductName") : tc("filterBySampleName")}
                  value={filterString}
                  onChange={setFilterString}
                />
              </InputGroup>
            </div>
            {shouldBeDoneSamples.length > 0 && (<FiAlertTriangle className="alert-triangle" title={tc("sampleInProgressOnDoneAnalysis")} />)}
            <div className="mb-3 sampleview-checkbox">
              <Form.Check
                checked={sortByProduct}
                type={"checkbox"}
                id={`sort-by-product`}
                label={tc("sortByProduct")}
                onChange={() => setSortByProduct(!sortByProduct)}
              />
              <Form.Check
                checked={includeDone}
                type={"checkbox"}
                id={`include-done-samples`}
                label={tc("includeDoneSamples")}
                onChange={() => setIncludeDone(!includeDone)}
              />
            </div>
            {!isReadOnly && (
              <div>
                <Button variant="success" onClick={() => setShowAddSample(true)}>
                  {isOnMobile ? tc("add") : tc("addSample")}
                </Button>
              </div>
            )}
          </div>

          {showAddSample && (
            <Popup onCancel={() => { resetNewSampleForm() }}>
              <AddEditSamplePopup
                title={tc("addSample")}
                initial={{
                  sampleName: newSample.sampleName,
                  analysisName: newSample.analysisName,
                  observationId: newSample.observationId,
                  arrival: newSample.arrival,
                  deadline: newSample.deadline ? `${newSample.deadline}T${newSample.deadlineTime}` : "",
                  priority: newSample.priority,
                  status: newSample.status,
                  productName: newSample.productName,
                  exclusive: newSample.exclusive,
                  isFlagged: newSample.isFlagged,
                }}
                analysisCollection={analysisCollection}
                productCollection={productCollection}
                onCancel={() => { resetNewSampleForm() }}
                onSubmit={(payload) => { submitNewSampleFromPopup(payload) }}
              />
            </Popup>
          )}

          {filteredSampleGroups.length !== 0 && !sortByProduct ? (
            filteredSampleGroups.flatMap((sample) => {
              if (!includeDone && sample.isDone) {
                return [] // skip this sample entirely
              }
              // Split analyses by observationId and create one row per observation id
              const analysesByObs = (sample.analyses || []).reduce((acc, a) => {
                const oid = a.observationId || "1"
                if (!acc[oid]) acc[oid] = []
                acc[oid].push(a)
                return acc
              }, {})

              return Object.entries(analysesByObs).map(([oid, analyses], index) => (
                <SampleRowElement
                  key={`${sample.sampleName}-${sample.productName || 'unknown'}-obs-${oid}-${index}`}
                  rowId={`${sample.sampleName}-${sample.productName || 'unknown'}-obs-${oid}-${index}`}
                  group={{ ...sample, analyses: analyses, observationId: oid, baseSampleName: sample.sampleName, sampleName: `${sample.sampleName}` }}
                  maxWidth={maxWidth}
                  activeSample={activeSample}
                  samplePriority={samplePriority}
                  filteredSampleGroups={filteredSampleGroups}
                  setActiveSampleAnalysis={setActiveSampleAnalysis}
                  setActiveOosId={setActiveOosId}
                  setActiveSample={setActiveSample}
                  setSamplePriority={setSamplePriority}
                  analysisCollection={analysisCollection}
                  productCollection={productCollection}
                  isReadOnly={isReadOnly}
                />
              ))
            })
          ) : filteredSampleGroups.length !== 0 && sortByProduct ? (
              filteredSampleGroups.map((product) => {
                const currentProductSamples = product[Object.keys(product)[0]]
                return (
                  <React.Fragment key={Object.keys(product)[0]}>
                    <h3>{Object.keys(product)[0] === "null" ? tc("samplesWithoutProduct") : Object.keys(product)[0]}</h3>
                    {currentProductSamples.flatMap((sample) => {
                      // split sample by observation id
                      const analysesByObs = (sample.analyses || []).reduce((acc, a) => {
                        const oid = a.observationId || "1"
                        if (!acc[oid]) acc[oid] = []
                        acc[oid].push(a)
                        return acc
                      }, {})

                      return Object.entries(analysesByObs).map(([oid, analyses], index) => (
                        <SampleRowElement
                          key={`${sample.sampleName}-${sample.productName || 'unknown'}-obs-${oid}-${index}`}
                          rowId={`${sample.sampleName}-${sample.productName || 'unknown'}-obs-${oid}-${index}`}
                          group={{ ...sample, analyses: analyses, observationId: oid, baseSampleName: sample.sampleName, sampleName: `${sample.sampleName}` }}
                          maxWidth={maxWidth}
                          activeSample={activeSample}
                          samplePriority={samplePriority}
                          filteredSampleGroups={sampleGroups}
                          setActiveSampleAnalysis={setActiveSampleAnalysis}
                          setActiveOosId={setActiveOosId}
                          setActiveSample={setActiveSample}
                          setSamplePriority={setSamplePriority}
                          isReadOnly={isReadOnly}
                        />
                      ))
                    })}
                  </React.Fragment>
                )
              })
            ) : (
                <h3>{tc("noSearchResults")}</h3>
              )}

          {Object.values(activeSampleAnalysis).length !== 0 && (
            <>
              {activeSampleAnalysis?.analysesPlanned ? (
                <Popup
                  onCancel={() => {
                    setActiveSampleAnalysis({})
                  }}
                  className="sampleview-task-table-popup"
                  center
                >
                  <div className="sampleview-task-table-popup-content">
                    <h4 className="sampleview-task-title">{activeSampleAnalysis.name}</h4>
                    <div className="sampleview-task-main">
                      <Table bordered hover className="SampleTable">
                        <thead>
                          <tr>
                            <th>{tc("tasks")}</th>
                            <th>{tc("collaborator")}</th>
                            <th>{tc("startEndDate")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeSampleAnalysis.tasks.map((task, idx) => (
                            <tr key={idx}>
                              <td>{task.name}</td>
                              <td>{task.collaborators.length !== 0 ? members[task.collaborators[0]].initials : ""}</td>
                              <td>
                                <ul>
                                  <li>{DateTime.fromISO(task.start).setZone(teamTimeZone).toFormat("ff")}</li>
                                  <li>{DateTime.fromISO(task.end).setZone(teamTimeZone).toFormat("ff")}</li>
                                </ul>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <div>
                      <h5 className="sampleview-task-title">{tc("sampleIdWithValue", { id: activeSampleAnalysis.observationId })}</h5>
                      {activeSampleAnalysis.customerName && (
                        <p>{tc("customerName")}: {activeSampleAnalysis.customerName}</p>
                      )}
                      <div className="sampletable-buttons">
                        {/* RR4 (#1): open the analysis on the plan with "QC Results & Approvals"
                            focused (focusQc, SPEC-022) — a LIMS work context. Gated on showLims: the
                            target view is LIMS-specific (the QC section doesn't render without LIMS),
                            so the button is hidden for non-LIMS teams. */}
                        {showLims && (
                          <Button variant="primary" onClick={handleGoToAnalysis}>
                            {tc("goToAnalysis")}
                          </Button>
                        )}
                        {!isReadOnly && (
                          <>
                          {(activeSampleAnalysis && activeSampleAnalysis.manual === true && !(activeSampleAnalysis.isDone === true || String(activeSampleAnalysis.status) === 'DONE')) && (
                            activeSampleAnalysis.finished ? (
                              <Button
                                variant="success"
                                onClick={() => {
                                  // Mark this planned sample observation as DONE
                                  const sid = activeSampleAnalysis.sampleId || activeSampleAnalysis.id || activeSampleAnalysis.sample_id || activeSampleAnalysis.sample?.id
                                  if (!sid) {
                                    dispatch(message.warning(t("adhoc.couldNotDetermineSampleIdDone")))
                                    return
                                  }
                                  dispatch(sampleView.markSampleDone(sid))
                                  setActiveSampleAnalysis({})
                                }}
                              >
                                {isOnMobile ? tc("done") : tc("markAsDone")}
                              </Button>
                            ) : (
                              // SPEC-047 R18: ghost the mark-done button when the analysis is NOT
                              // finished (its last task still lies in the future). The BE refuses
                              // POST /sample/<id>/mark-done with 406 until `finished`, so an active
                              // button here would only fire a dead 406. Mirror the unplanBlocked
                              // ghost pattern below (blocked-action-wrap + disabled blocked-action-btn
                              // so the tooltip + not-allowed cursor show). Ghost-not-hide (SPEC-045
                              // K11). `finished` undefined (older BE) is treated as NOT finished —
                              // fail-closed; the BE guard is authoritative regardless.
                              <span className="blocked-action-wrap" title={tc("markDoneNotFinished")}>
                                <Button variant="secondary" className="blocked-action-btn" disabled>
                                  {isOnMobile ? tc("done") : tc("markAsDone")}
                                </Button>
                              </span>
                            )
                          )}
                          {unplanBlocked ? (
                            // RR5 P6 / SPEC-045 K2: ghost the Unplan button HARD (clearly inactive
                            // grey, not a faded red that still reads as clickable). The wrapper span
                            // carries the not-allowed cursor + tooltip — a disabled <button> has
                            // pointer-events:none and would show neither. Reason-aware tooltip
                            // (SPEC-046 F4): auto-locked past analysis → "move it forward";
                            // manually locked future analysis → "unlock it"; otherwise the LIMS
                            // QC-in-progress reason (unchanged).
                            <span
                              className="blocked-action-wrap"
                              title={
                                activeAnalysisLocked || activeAnalysisStarted
                                  ? tc(activeAnalysisStarted ? "blockedActionStarted" : "blockedActionLocked")
                                  : tc("limsUnplanBlockedQc")
                              }
                            >
                              <Button variant="secondary" className="blocked-action-btn" disabled>
                                {isOnMobile ? tc("unplan") : tc("unplanSample")}
                              </Button>
                            </span>
                          ) : (
                            <Button
                              variant="danger"
                              onClick={() => {
                                dispatch(samples.unplanSamples(activeSampleAnalysis.analysisId, [activeSampleAnalysis.sampleId]))
                                setActiveSampleAnalysis({})
                              }}
                            >
                              {isOnMobile ? tc("unplan") : tc("unplanSample")}
                            </Button>
                          )}
                          {!showLims && (
                            activeSampleAnalysis.started ? (
                              <Button
                                className="sampletable-fail-button"
                                onClick={() => setFailConfirm(true)}
                              >
                                {isOnMobile ? tc("fail") : tc("failSample")}
                              </Button>
                            ) : (
                              // SPEC-047 (R19): ghost Fail until the analysis has STARTED (its
                              // earliest task lies today or earlier) — the backend refuses fail
                              // with 406 before then; failing asserts the run was attempted, so it
                              // cannot be claimed ahead of time. `started` is the authoritative
                              // sample-view flag (mirrors the R18 `finished` ghost above);
                              // undefined (older BE) is treated as NOT started — fail-closed.
                              <span className="blocked-action-wrap" title={tc("blockedActionFailNotStarted")}>
                                <Button variant="secondary" className="blocked-action-btn" disabled>
                                  {isOnMobile ? tc("fail") : tc("failSample")}
                                </Button>
                              </span>
                            )
                          )}
                          {!activeSampleAnalysis.isFlagged ? (
                            <Button variant="warning" onClick={handleFlagSample}>
                              {isOnMobile ? tc("flag") : tc("flagSample")}
                            </Button>
                          ) : (
                              <Button variant="warning" onClick={handleUnflagSample}>
                                {isOnMobile ? tc("unflag") : tc("unflagSample")}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Popup>
              ) : (
                  <>
                    {shouldShowUnplannedPopup && (
                      <>
                        <Popup
                          singleMessage={
                            <>
                              <p>
                                <b>{activeSampleAnalysis.name}</b>
                              </p>
                              <p>{tc("sampleIdWithValue", { id: activeSampleAnalysis.observationId })}</p>
                              <p>{tc("analysisNotPlanned")}</p>
                              { !isReadOnly && (activeSampleAnalysis && (activeSampleAnalysis.manual === true || String(activeSampleAnalysis.manual) === 'true')) && (
                                <div style={{ marginTop: '0.5rem' }}>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => {
                                      // compute id immediately and store so it doesn't vanish if activeSampleAnalysis is cleared
                                      const sid = activeSampleAnalysis && (
                                        activeSampleAnalysis.sampleId ||
                                        activeSampleAnalysis.id ||
                                        activeSampleAnalysis.sample_id ||
                                        activeSampleAnalysis.sample?.id ||
                                        activeSampleAnalysis.observationId ||
                                        null
                                      )
                                      if (!sid) {
                                        dispatch(message.warning(t("adhoc.couldNotDetermineSampleIdDelete")))
                                        return
                                      }
                                      setPendingDeleteSampleId(sid)
                                      setDeleteConfirm(true)
                                    }}
                                  >
                                    {isOnMobile ? tc("delete") : tc("deleteSample")}
                                  </Button>
                                </div>
                              )}
                            </>
                          }
                          onCancel={() => setActiveSampleAnalysis({})}
                          position={{
                            top: activeSampleAnalysis.position?.top - 180,
                            left: activeSampleAnalysis.position?.left,
                            height: activeSampleAnalysis.position?.height,
                          }}
                        />
                      </>
                    )}
                  </>
                )}
            </>
          )}
          {failConfirm && (
            <ConfirmDialog
              onCancel={() => {
                setFailConfirm(false)
              }}
              onConfirm={() => {
                // SPEC-032: fail this sample → backend creates a rerun (DELETE /samples/fail-samples).
                dispatch(
                  samples.failSamples(
                    activeSampleAnalysis.analysisId,
                    [activeSampleAnalysis.sampleId],
                    true,
                    activeSampleAnalysis.sampleName,
                    activeSampleAnalysis.productName,
                    activeSampleAnalysis.observationId
                  )
                )
                setActiveSampleAnalysis({})
                setFailConfirm(false)
              }}
            >
              <Container>
                <div className="FailSamples-info-text">
                  <FiInfo title={tc("confirmFailSamplesInfo")} />
                </div>
                <h4>{tc("confirmFailSamples")}</h4>
              </Container>
            </ConfirmDialog>
          )}
          {deleteConfirm && (
            <ConfirmDialog
              onCancel={() => {
                setDeleteConfirm(false)
                setPendingDeleteSampleId(null)
              }}
              onConfirm={() => {
                const sid = pendingDeleteSampleId || (activeSampleAnalysis && (
                  activeSampleAnalysis.sampleId ||
                  activeSampleAnalysis.id ||
                  activeSampleAnalysis.sample_id ||
                  activeSampleAnalysis.sample?.id ||
                  activeSampleAnalysis.observationId ||
                  null
                ))
                if (!sid) {
                  dispatch(message.warning(t("adhoc.couldNotDetermineSampleIdDeleteMissing")))
                  setDeleteConfirm(false)
                  setPendingDeleteSampleId(null)
                  setActiveSampleAnalysis({})
                  return
                }
                dispatch(sampleView.deleteSample(sid)).then(() => {
                  setActiveSampleAnalysis({})
                }).catch(() => {})
                setDeleteConfirm(false)
                setPendingDeleteSampleId(null)
              }}
              submitName={isOnMobile ? tc("delete") : tc("deleteSample")}
            >
              <Container>
                <div className="FailSamples-info-text">
                  <FiInfo title={tc("confirmDeleteSampleInfo")} />
                </div>
                <h4>{tc("confirmDeleteSample")}</h4>
              </Container>
            </ConfirmDialog>
          )}

          {showLims && activeOosId && (
            <OosInvestigationModal
              oosId={activeOosId}
              onClose={() => setActiveOosId(null)}
              onClosed={() => setActiveOosId(null)}
            />
          )}
        </div>
      </>
    )
  } else {
    return (
      <div className="SampleView">
        <h3>{tc("noSamplesAvailable")}</h3>
      </div>
    )
  }
}
